/**
 * shaders/PointsShader
 *
 */

import { goldNoiseFns, toVar } from './core';
import ColorManager from '../core/color-manager';
import { hexToArr, noopTrue, repeatStr } from '../core/utils';


export default class PointsShader {

    constructor(layers, config) {
        this.layers = layers;
        this.dataTile = toVar(config.fields.primary);

        if (config.filter != noopTrue) {
            let filter = config.filter.toString();
            const paramNames = filter.substring(filter.indexOf('(') + 1, filter.indexOf(')')).split(',').map(p => p.trim());

            filter = filter.substring(filter.indexOf('return'), filter.length - 1).split('return')[1];

            this.calculatedFields = config.fields.all.map(field => filter.indexOf(field) !== -1 ? field : null).filter(x => !!x);

            if (filter.indexOf(';') !== -1) {
                filter = filter.substring(0, filter.indexOf(';'));
            }

            filter = filter.split(`${paramNames[0]}.`).join('c_');
            filter = filter.split(`${paramNames[1]}.`).join('u_');

            this.filter = filter.trim();
        }
    }


    /**
     * @param Object<Boolean> bools
     * @return Number
     */
    checksToBits(bools) {
        let num = 0;
        let m = 1;

        for (let i = this.layers.length - 1; i >= 0; i--) {
            const k = this.layers[i];
            if (bools[k]) {
                num += m;
            }
            m = m * 2;
        }

        return num;
    }


    /**
     * @param ColorManager palette
     * @return String
     */
    getFragmentShader(palette) {
        const colors = [];

        this.layers.forEach(layer => {
            colors.push(hexToArr(palette.get(layer)));
        });

        const colorStrs = [`vec3 colors[${this.layers.length}];`];
        colors.forEach((color, i) => {
            const cStrs = color.map(b => b.toFixed(3)).join(', ');
            colorStrs.push(` colors[${i}] = vec3(${cStrs});`);
        });

        return `
${goldNoiseFns(this.layers.length)}

//NOTE replacing built-in mod because on phones mod of a float can be subject to precision issues
// float v1 = 8730.;
// float v2 = 212.;
//(mod(v1, v2) == mod(8730., 212.))
float floor2(float val) {
    float rounded = float(int(val));
    if (rounded > val) return rounded - 1.;
    return rounded;
}

float mod2(float n, float d) {
    if (d == 0.) {
        return 0.;
    }
    return n - d * floor2(n / d);
}

//coordinate is the actual pixel coordinate
vec2 applyOffsets(vec2 coordinate, float x, float y, float scale, float idxSide, vec2 idxOffset) {
    float ts = 1.0 / idxSide;
    float ox = idxOffset.x / idxSide;
    float oy = idxOffset.y / idxSide;
    vec2 val = vec2(ox + coordinate.x / (scale * idxSide) + x * ts, oy + coordinate.y / (scale * idxSide) + y * ts);
    return vec2(floor2(val.x * 512.0) / 512.0, floor2(val.y * 512.0) / 512.0);
}

void adjustForAdjacent(inout vec2 offsets, float xOffset) {
    float t00_T = 256. / 512.;
    float t00_B = 257. / 512.;
    float t00_L = 258. / 512.;
    float t00_R = 259. / 512.;

    float t10_T = 260. / 512.;
    float t10_B = 261. / 512.;
    float t10_L = 262. / 512.;
    float t10_R = 263. / 512.;

    if (xOffset == 1.) {
        float ox = offsets.x - 0.5;
        if (offsets.y < 0.) {
            offsets.x = ox;
            offsets.y = t10_T;
        } else if (offsets.y >= 0.5) {
            offsets.x = ox;
            offsets.y = t10_B;
        }
        if (ox < 0.) {
            offsets.x = offsets.y;
            offsets.y = t10_L;
        } else if (ox >= 0.5) {
            offsets.x = offsets.y;
            offsets.y = t10_R;
        }
    } else {
        if (offsets.y < 0.) {
            offsets.y = t00_T;
        } else if (offsets.y >= 0.5) {
            offsets.y = t00_B;
        }
        if (offsets.x < 0.) {
            offsets.x = offsets.y;
            offsets.y = t00_L;
        } else if (offsets.x >= 0.5) {
            offsets.x = offsets.y;
            offsets.y = t00_R;
        }
    }
}

int toVal(vec4 texelColor) {
    int r = int(texelColor.r * 255.);
    int g = int(texelColor.g * 255.);
    int b = int(texelColor.b * 255.);

    return r * 256 * 256 + g * 256 + b;
}

bool categoryMatch(vec4 color, float index) {
    return abs(color.b - index / 255.0) < 0.00001;
}

vec3 rgb(float r,float g,float b) {
    return vec3(r/255., g/255., b/255.);
}

const int max_its = 1000;//max number of entities on 1 pixel

void calcColors(vec2 coordinate, float cntStep, inout float colorCnts[${this.layers.length}]) {
    float scale = uImageScale0;// * 2.;
    vec2 offsets1 = applyOffsets(coordinate, uOffsetX0, uOffsetY0, scale, 2., vec2(0., 0.));
    adjustForAdjacent(offsets1, 0.);

    vec4 startVal = texture2D(uTexture0, offsets1);

    float tw = u_dataTileWidth;

    if (startVal.a == 0.0) {
        return;
    }

    vec2 offsets2 = applyOffsets(coordinate, uOffsetX0, uOffsetY0, scale, 2., vec2(1., 0.));
    adjustForAdjacent(offsets2, 1.);

    vec4 stopVal = texture2D(uTexture0, offsets2);

    int v_start = toVal(startVal);
    int v_stop = toVal(stopVal);

    float r = 0.0;

    int f_start = toVal(startVal);//TODO check calc
    int f_stop = toVal(stopVal);

    for (int c = 0; c < max_its; c++) {
        int i = c + f_start;
        if (i > f_stop) { break; }
       // r = 0.2;
        //process all elements in array between start and stop... these are record values matching this pixel
        //convert this int (i) into a pixel position, then grab the value from the appropriate pixel
        float x = mod2(float(i), tw);
        float y = floor2(float(i)/tw);
        vec2 dataTileCoord = vec2((0.5+x)/tw, (0.5+y)/tw);
        vec4 color = texture2D(u_${this.dataTile}, dataTileCoord);
${
    this.filter ? (
        this.calculatedFields.map(field => (
        `
        vec4 v${field} = texture2D(u_${toVar(field)}, dataTileCoord);
        float c_${field} = float(toVal(v${field}));
        `
        )).join('\n')
    ) : ''
}

        ${this.filter ? `if (${this.filter}) {` : ''}
            ${repeatStr(`if (categoryMatch(color, #.)) {
                colorCnts[#] = colorCnts[#] + cntStep;
            }`, this.layers.length)}
        ${this.filter ? '}' : ''}
    }
}

void main(void) {
    float colorCnts[${this.layers.length}];
    ${repeatStr('colorCnts[#] = 0.;', this.layers.length)}

    float px1 = 1. / 256.;
    float starFade = 0.7;

    calcColors(textureCoord, 1.0, colorCnts);
    calcColors(textureCoord + vec2(px1,0.), starFade, colorCnts);
    calcColors(textureCoord + vec2(-px1,0.), starFade, colorCnts);
    calcColors(textureCoord + vec2(0.,px1), starFade, colorCnts);
    calcColors(textureCoord + vec2(0.,-px1), starFade, colorCnts);

    //a very complicated way to apply a bit-mask!
    float f = u_filter;
    int count = ${this.layers.length};//needs to match # of elements used to create bit-mask
    for (int c = 0; c < ${this.layers.length}; c++) {
        float num = pow(2.0, float(count - c - 1));
        if (f >= num) {
            f = f - num;
        } else {
            colorCnts[c] = 0.;
        }
    }

    ${colorStrs.join('\r\n')}

    gl_FragColor = goldNoiseBlend(u_seed, textureCoord, colorCnts, colors, u_fade);
}
        `;
    }
}
